/**
 * GHL walkthrough-calendar fields stored on `client-prospects`.
 * Matches the appointment "View details" keys: timezone, calendarName,
 * source, selectedSlot, and page URL.
 */

export const GHL_WALKTHROUGH_CALENDAR_NAME = 'Posture Assessment Walkthrough Call';

export interface GhlBookingRecord {
  timezone?: string;
  selectedTimezone?: string;
  calendarName?: string;
  source?: string;
  selectedSlot?: string;
  pageUrl?: string;
  email?: string;
  name?: string;
  updatedAt: string;
}

const MAX_TZ = 80;
const MAX_SLOT = 64;
const MAX_NAME = 200;
const MAX_EMAIL = 320;
const MAX_URL = 500;

function clampText(value: unknown, max: number): string {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, max);
}

export function sanitizeGhlTimezone(value: unknown): string | undefined {
  const raw = clampText(value, MAX_TZ);
  if (!raw) return undefined;
  if (raw === 'UTC' || raw === 'Etc/UTC') return raw;
  if (!/^[A-Za-z0-9_+\-/]+$/.test(raw) || !raw.includes('/')) return undefined;
  return raw;
}

export function sanitizeGhlSlot(value: unknown): string | undefined {
  const raw = clampText(value, MAX_SLOT);
  if (!raw) return undefined;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return undefined;
  return raw;
}

export function sanitizeGhlPageUrl(value: unknown): string | undefined {
  const raw = clampText(value, MAX_URL);
  if (!raw) return undefined;
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return undefined;
    parsed.searchParams.delete('harry_sa_session');
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return undefined;
  }
}

function pickString(record: Record<string, unknown>, keys: string[], max: number): string {
  for (const key of keys) {
    const value = clampText(record[key], max);
    if (value) return value;
  }
  return '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function buildHarryGhlBookingSnapshot(input: {
  timezone?: string;
  pageUrl?: string;
}): GhlBookingRecord {
  const timezone = sanitizeGhlTimezone(input.timezone);
  const pageUrl = sanitizeGhlPageUrl(input.pageUrl);
  return {
    ...(timezone ? { timezone, selectedTimezone: timezone } : {}),
    calendarName: GHL_WALKTHROUGH_CALENDAR_NAME,
    source: GHL_WALKTHROUGH_CALENDAR_NAME,
    ...(pageUrl ? { pageUrl } : {}),
    updatedAt: new Date().toISOString(),
  };
}

export function mergeGhlBooking(
  existing: Partial<GhlBookingRecord> | undefined,
  incoming: GhlBookingRecord,
): GhlBookingRecord {
  const next: GhlBookingRecord = {
    ...(existing ?? {}),
    updatedAt: incoming.updatedAt,
  };
  const keys: (keyof GhlBookingRecord)[] = [
    'timezone',
    'selectedTimezone',
    'calendarName',
    'source',
    'selectedSlot',
    'pageUrl',
    'email',
    'name',
  ];
  for (const key of keys) {
    const value = incoming[key];
    if (typeof value === 'string' && value.trim()) {
      next[key] = value;
    }
  }
  return next;
}

export type ParseGhlBookingResult =
  | { ok: true; booking: GhlBookingRecord }
  | { ok: false; error: string };

/**
 * Accept GHL workflow / appointment webhook shapes and the calendar
 * form-submission fields shown on the event (selectedSlot, selectedTimezone).
 */
export function parseGhlBookingPayload(body: unknown): ParseGhlBookingResult {
  const root = asRecord(body);
  if (!root) return { ok: false, error: 'Invalid JSON body' };

  const calendar = asRecord(root.calendar) ?? {};
  const appointment = asRecord(root.appointment) ?? {};
  const page = asRecord(root.page) ?? {};
  const custom = asRecord(root.customData) ?? asRecord(root.custom_fields) ?? {};
  const contact = asRecord(root.contact) ?? {};

  const email = clampText(
    root.email ?? contact.email ?? root.contact_email,
    MAX_EMAIL,
  ).toLowerCase();
  if (!email || !email.includes('@')) {
    return { ok: false, error: 'Missing contact email' };
  }

  const timezone =
    sanitizeGhlTimezone(root.timezone) ??
    sanitizeGhlTimezone(root.selectedTimezone) ??
    sanitizeGhlTimezone(calendar.selectedTimezone) ??
    sanitizeGhlTimezone(contact.timezone);

  const selectedTimezone =
    sanitizeGhlTimezone(root.selectedTimezone) ??
    sanitizeGhlTimezone(calendar.selectedTimezone) ??
    timezone;

  const selectedSlot =
    sanitizeGhlSlot(root.selectedSlot) ??
    sanitizeGhlSlot(calendar.startTime) ??
    sanitizeGhlSlot(calendar.start_time) ??
    sanitizeGhlSlot(appointment.start_time) ??
    sanitizeGhlSlot(appointment.startTime);

  const calendarName =
    pickString(root, ['calendarName', 'calendar_name'], MAX_NAME) ||
    pickString(calendar, ['calendarName', 'name', 'title'], MAX_NAME) ||
    undefined;

  const source =
    pickString(root, ['source', 'calendarName'], MAX_NAME) ||
    pickString(calendar, ['source', 'calendarName', 'name'], MAX_NAME) ||
    calendarName;

  const pageUrl =
    sanitizeGhlPageUrl(root.url) ??
    sanitizeGhlPageUrl(root.pageUrl) ??
    sanitizeGhlPageUrl(page.url) ??
    sanitizeGhlPageUrl(custom.harry_posture_analysis_url) ??
    sanitizeGhlPageUrl(root.harry_posture_analysis_url);

  const name =
    pickString(root, ['full_name', 'name'], MAX_NAME) ||
    pickString(contact, ['full_name', 'name'], MAX_NAME) ||
    [clampText(root.first_name ?? contact.first_name, 100), clampText(root.last_name ?? contact.last_name, 100)]
      .filter(Boolean)
      .join(' ') ||
    email;

  const booking: GhlBookingRecord = {
    email,
    name,
    ...(timezone ? { timezone } : {}),
    ...(selectedTimezone ? { selectedTimezone } : {}),
    ...(calendarName ? { calendarName } : {}),
    ...(source ? { source } : {}),
    ...(selectedSlot ? { selectedSlot } : {}),
    ...(pageUrl ? { pageUrl } : {}),
    updatedAt: new Date().toISOString(),
  };

  return { ok: true, booking };
}

export function hasGhlSelectedSlot(booking: Partial<GhlBookingRecord> | undefined): boolean {
  return Boolean(booking?.selectedSlot);
}
